import { createFormContext } from "@mantine/form";
import { StrictUndef } from "shared/api/httpClient";
import { createRandomStr, to } from "shared/utils/fns";
import { some } from "shared/utils/maybe";
import { geozod, nonemptyString, nonemptyUUID } from "shared/utils/zod";
import { Cm, ComposeDetailsFragment, DraftInfoInput, Language, MessageSeverity } from "src/gql/graphql";
import { DraftInfoInputSchema, DurationInfoInputSchema } from "src/gql/zod";
import { z } from "zod";

export const composeFormStateSchema = z.object({
    audience: z.object({
        groups: z
            .object({
                id: z.string(),
                name: z.string(),
                internalJoinable: z.boolean(),
                synchronized: z.boolean(),
            })
            .array(),
        topics: z.object({ id: z.string(), name: z.string() }).array(),
        users: z.string().array(),
        features: geozod.feature(geozod.polygon()).array(),
        geofilters: z.string().uuid().array(),
    }),
    duration: DurationInfoInputSchema()
        .default({ span: 1440, absolute: null })
        .refine((value) => (value.absolute != null ? Number(value.absolute) > Date.now() : true), {
            path: ["absolute"],
            message: "Expiration must be in the future.",
        }),
    customizations: z.object({
        sms: z.object({
            value: z.string(),
            enabled: z.boolean(),
        }),
        phone: z.object({
            value: z.string(),
            enabled: z.boolean(),
        }),
    }),
    sender: z.object({ id: nonemptyUUID() }),
    severity: z.nativeEnum(MessageSeverity),
    subject: nonemptyString(),
    body: z.string(),
    richBody: z.string(),
    files: z.object({ id: nonemptyUUID() }).array(),
    options: z.object({ method: z.nativeEnum(Cm) }).array(),
    acknowledgable: z.boolean(),
    escalations: z.object({ delayMin: z.number() }).array(),
    poll: z
        .object({
            content: z.string().nonempty(),
            options: z
                .object({ id: z.string(), content: z.string().nonempty(), isSelected: z.boolean() })
                .array()
                .min(1, "At least one option must be provided."),
        })
        .nullish(),
    meta: z
        .object({
            mode: z.enum(["edit", "read"]).default("edit"),
        })
        .default({}),
});

export type ComposeFormState = z.infer<typeof composeFormStateSchema>;

export const [ComposeFormProvider, useComposeFormContext, useComposeForm] = createFormContext<ComposeFormState>();

export function createComposeFormState(incoming: StrictUndef<ComposeDetailsFragment>): ComposeFormState {
    const { duration } = incoming;

    return {
        ...incoming,
        duration: duration
            ? duration.span
                ? { span: duration.span, absolute: null }
                : { absolute: duration.absolute, span: null }
            : { span: 1440, absolute: null },
        escalations: incoming.escalations.map(({ delaySec }) => ({
            delayMin:
                some(delaySec)
                    .if((it) => it >= 60 * 5)
                    ?.take((it) => Math.floor(it / 60)) ?? 5,
        })),
        customizations: {
            sms: incoming.customizations.find((it) => it.method == Cm.Sms) ?? { enabled: false, value: "" },
            phone: incoming.customizations.find((it) => it.method == Cm.Phone) ?? { enabled: false, value: "" },
        },
        audience: {
            ...incoming.audience,
            users: incoming.audience.users.map(to("id")),
            geofilters: incoming.audience.geofilters.map(to("id")),
            features: incoming.audience.geometries
                .map((s) => JSON.parse(s))
                .map((geometry) => geozod.feature(geozod.polygon()).parse({ type: "Feature", id: createRandomStr(), geometry })),
        },
        meta: {
            mode: "edit",
        },
    };
}

export const composeFormStateToDraftInfoInput = composeFormStateSchema.transform((state) =>
    DraftInfoInputSchema().parse({
        ...state,
        sender: state.sender.id,
        audience: {
            geofilters: state.audience.geofilters,
            users: state.audience.users,
            groups: state.audience.groups.map(to("id")),
            topics: state.audience.topics.map(to("id")),
            geometries: state.audience.features.map((f) => JSON.stringify(f.geometry)),
        },
        files: state.files.map(to("id")),
        escalations: state.severity == MessageSeverity.Urgent ? state.escalations.map((it) => ({ delaySec: it.delayMin * 60 })) : [],
        acknowledgable: state.severity == MessageSeverity.Urgent && state.acknowledgable,
        locales: [
            {
                language: Language.English,
                body: state.body,
                richBody: state.richBody,
                subject: state.subject,
                customizations: [
                    { method: Cm.Sms, ...state.customizations.sms },
                    { method: Cm.Phone, ...state.customizations.phone },
                ],
            },
        ],
    } satisfies DraftInfoInput),
);
